import type { Attachment } from './types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
  }
  /** Network failure, server error or throttling: worth retrying later. */
  get retryable() {
    return this.status === 0 || this.status >= 500 || this.status === 429;
  }
}

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

export async function api<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network_error');
  }
  const data = res.headers.get('content-type')?.includes('json') ? await res.json().catch(() => ({})) : {};
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/api/auth/')) onUnauthorized?.();
    throw new ApiError(res.status, data.error ?? 'http_error');
  }
  return data as T;
}

/** Multipart upload with progress (fetch has no upload progress events). */
export function uploadFile<T>(
  path: string,
  file: Blob,
  fileName: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as T);
      else {
        if (xhr.status === 401) onUnauthorized?.();
        reject(new ApiError(xhr.status, xhr.response?.error ?? 'upload_failed'));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'network_error'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted'));
    signal?.addEventListener('abort', () => xhr.abort());
    const form = new FormData();
    form.append('file', file, fileName);
    xhr.send(form);
  });
}

export const uploadAttachment = (
  conversationId: string,
  file: Blob,
  fileName: string,
  onProgress?: (f: number) => void,
  voice?: { durationMs: number },
) =>
  uploadFile<{ attachment: Attachment }>(
    `/api/conversations/${conversationId}/attachments${voice ? `?voice=1&durationMs=${Math.round(voice.durationMs)}` : ''}`,
    file,
    fileName,
    onProgress,
  );

export const ERROR_TEXT: Record<string, string> = {
  network_error: 'Нет соединения с сервером',
  invalid_credentials: 'Неверный логин или пароль',
  phone_taken: 'Этот номер уже зарегистрирован',
  username_taken: 'Этот ник уже занят',
  wrong_password: 'Неверный текущий пароль',
  reset_unavailable: 'Восстановление пароля сейчас недоступно. Свяжитесь с администратором напрямую.',
  validation_error: 'Проверьте правильность заполнения полей',
  rate_limited: 'Слишком много попыток, попробуйте позже',
  file_too_large: 'Файл слишком большой',
  unsupported_file_type: 'Этот тип файла не поддерживается',
  empty_file: 'Файл пустой',
  file_lost: 'Файл не сохранился — прикрепите его заново',
};

export const errorText = (e: unknown) =>
  e instanceof ApiError ? (ERROR_TEXT[e.code] ?? `Ошибка (${e.code})`) : 'Неизвестная ошибка';
